import type { GrowthBook } from '@growthbook/growthbook'
import type { growthbookTrackingPlugin } from '@growthbook/growthbook/plugins'

const INIT_TIMEOUT_MS = 1000

// Max events held in memory while waiting for a consent decision
const MAX_BUFFERED_EVENTS = 100

export type GrowthBookAttributes = {
  id?: string | number
  deviceId?: string
  device_id?: string
  loggedIn: boolean
  planTier?: string
  country?: string
  siteSlug?: string
}

type EventLogger = Parameters<GrowthBook['setEventLogger']>[0]
type TrackingPlugin = typeof growthbookTrackingPlugin

let instance: GrowthBook | null = null
let cleanupTracking: (() => void) | null = null

export function getGrowthBook(): GrowthBook {
  if (!instance) {
    throw new Error(
      'GrowthBook has not been initialised — call initGrowthBook() first'
    )
  }
  return instance
}

export async function initGrowthBook(): Promise<GrowthBook> {
  const clientKey = import.meta.env.GLP_GROWTHBOOK_CLIENT_KEY

  // Loaded on demand so the SDK isn't in the main bundle when GrowthBook is off
  const [{ GrowthBook }, { growthbookTrackingPlugin: trackingPlugin }] =
    await Promise.all([
      import('@growthbook/growthbook'),
      import('@growthbook/growthbook/plugins'),
    ])

  instance = new GrowthBook({
    apiHost:
      import.meta.env.GLP_GROWTHBOOK_API_HOST || 'https://cdn.growthbook.io',
    clientKey,
    enableDevMode: import.meta.env.GLP_APP_ENV !== 'production',
  })

  // Remove any listeners left by a previous initGrowthBook call
  cleanupTracking?.()
  cleanupTracking = null

  // tracking requires clientKey — omit when not configured (e.g. local dev)
  if (clientKey) {
    cleanupTracking = setUpConsentGatedTracking(instance, trackingPlugin)
  } else {
    instance.setEventLogger(async () => {})
  }

  try {
    await instance.init({ timeout: INIT_TIMEOUT_MS })
  } catch {
    if (import.meta.env.GLP_APP_ENV !== 'production') {
      console.warn('[GrowthBook] init timed out — using default variants')
    }
  }

  return instance
}

/**
 * Consent gate for GrowthBook tracking. The SDK has no built-in consent
 * management: growthbookTrackingPlugin forwards every event to the ingestor
 * the moment it is called. We intercept with a buffering logger on init, then
 * install the real plugin only when the visitor accepts statistics cookies.
 *
 * Events buffered before a decision are replayed in order on accept, or
 * discarded on decline. Experiment assignment (run()) is unaffected so this
 * never delays feature-flag checks or rendering.
 */
function setUpConsentGatedTracking(
  gb: GrowthBook,
  trackingPlugin: TrackingPlugin
): () => void {
  let buffer: Parameters<EventLogger>[] | null = []
  let trackingLogger: EventLogger | null = null

  gb.setEventLogger(async (...args) => {
    if (buffer && buffer.length < MAX_BUFFERED_EVENTS) {
      buffer.push(args)
    }
  })

  const enableTracking = () => {
    if (!trackingLogger) {
      // Capture the logger the plugin registers so it can be restored if
      // consent is withdrawn and later given again
      const setEventLogger = gb.setEventLogger.bind(gb)
      gb.setEventLogger = (logger) => {
        trackingLogger = logger
        setEventLogger(logger)
      }
      trackingPlugin({
        ingestorHost: import.meta.env.GLP_GROWTHBOOK_INGESTOR_HOST || undefined,
      })(gb)
      gb.setEventLogger = setEventLogger
    }

    if (!trackingLogger) {
      return
    }

    gb.setEventLogger(trackingLogger)
    const pending = buffer ?? []
    buffer = null
    // Replay sequentially so buffered events arrive in order.
    // Catch per step so one rejection doesn't drop the remaining events.
    void pending.reduce(
      (chain, args) =>
        chain.then(() => trackingLogger?.(...args)).catch(() => {}),
      Promise.resolve()
    )
  }

  const disableTracking = () => {
    buffer = null
    gb.setEventLogger(async () => {})
  }

  const syncWithConsent = () => {
    // Cookiebot's script loads after mount, so it may not exist yet
    const cookiebot = window.Cookiebot as Window['Cookiebot'] | undefined
    if (!cookiebot?.hasResponse) {
      return
    }

    if (cookiebot.consent?.statistics) {
      enableTracking()
    } else {
      disableTracking()
    }
  }

  // Consent may already be known (returning visitor) or arrive later
  syncWithConsent()
  window.addEventListener('CookiebotOnConsentReady', syncWithConsent)
  window.addEventListener('CookiebotOnAccept', syncWithConsent)
  window.addEventListener('CookiebotOnDecline', syncWithConsent)

  return () => {
    window.removeEventListener('CookiebotOnConsentReady', syncWithConsent)
    window.removeEventListener('CookiebotOnAccept', syncWithConsent)
    window.removeEventListener('CookiebotOnDecline', syncWithConsent)
  }
}

/**
 * Merge attributes into the GrowthBook instance. Uses updateAttributes so
 * existing attributes are kept.
 * No-op when GrowthBook hasn't been initialised so callers (e.g. auth flows)
 * never fail because of experimentation.
 */
function setGrowthBookAttributes(attrs: GrowthBookAttributes): void {
  void instance?.updateAttributes(attrs)
}

/**
 * Sync the current user (or guest, when null) to GrowthBook. User-specific
 * keys are always passed so they're cleared on logout rather than merged.
 */
export function setGrowthBookUser(
  user: {
    id?: string | number
    tier?: { plan_tier?: string }
    country?: string
  } | null
): void {
  let deviceId: string | undefined
  try {
    deviceId = localStorage.getItem('device_id') ?? undefined
  } catch {
    // localStorage unavailable (e.g. Safari private mode with full quota)
  }

  setGrowthBookAttributes({
    id: user?.id,
    deviceId,
    device_id: deviceId, // snake_case maps to the warehouse device_id column
    loggedIn: !!user,
    planTier: user?.tier?.plan_tier,
    country: user?.country,
    siteSlug: import.meta.env.GLP_SITE_SLUG,
  })
}
